import { createContext, useContext, useEffect, useState } from 'react';
import { fetchCodes, fetchInfo, submitInfo } from './helpers';
import { CostingRequestsContext } from '../../../contexts/CostingRequest';
import { showAlert } from '../../../utils/alerts';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../../routes';

export const FormItem = {
  sku: undefined,
  sku_description: undefined,
  buyer_country: undefined,
  brewer_country: undefined,
  buyer_code: undefined,
  brewer_code: undefined,
  currency: undefined,
  interco_markup_out_perf: undefined,
  transfer_price: undefined,
};

export const Context = createContext();

export const Provider = ({ children }) => {
  const navigate = useNavigate();
  const { skus: skusList, getSkus } = useContext(CostingRequestsContext);
  const [form, setForm] = useState([FormItem]);
  const [skus, setSkus] = useState([]);
  const [countries, setCountries] = useState([]);
  const [codes, setCodes] = useState([]);
  const [codesCountry, setCodesCountry] = useState({});
  const [initialLoading, setInitialLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const [pir, setPir] = useState([]);

  useEffect(() => {
    initialize();

    return () => reset();
  }, []);

  useEffect(() => {
    if (skusList.length > 0) {
      setSkus(
        skusList.map((sku) => {
          return {
            label: `${sku.sku} | ${sku.sku_description}`,
            value: `${sku.sku}_${sku.sku_description}`,
          };
        }),
      );
    }
  }, [skusList]);

  const initialize = async () => {
    try {
      setInitialLoading(true);
      const [codes] = await Promise.all([fetchCodes(), getSkus()]);
      setCountries([...new Set(Object.values(codes))]);
      setCodes(codes);
    } catch (error) {
      console.error('Error initializing state:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  const reset = () => {
    setForm([]);
    setPir([]);
  };

  const onFormChange = (key, name, value) => {
    const currentValues = [...form];
    currentValues[key][name] = value;

    if (name === 'sku') {
      const [sku, sku_description] = value.split('_');
      currentValues[key][name] = sku;
      currentValues[key]['sku_description'] = sku_description;
    }

    if (['buyer_code', 'brewer_code'].includes(name)) {
      const [code, country] = value.split('_');
      currentValues[key][name] = code;
    }

    setForm(currentValues);
  };

  const canSubmit = () => form.sku && form.brewer_country && form.buyer_country;

  const fetch = async () => {
    try {
      setLoading(true);
      const data = await fetchInfo(form);

      const pirData = [];
      const formSelections = [...form];
      const errorRows = [];

      data.forEach((item) => {
        const rowIndex = formSelections.findIndex((i) => parseInt(i.sku) === parseInt(item.sku));
        const row = formSelections[rowIndex];
        formSelections[rowIndex] = { ...row, error: !item.found };

        if (!row || !item.found) {
          errorRows.push(rowIndex + 1);
          return;
        }

        const tpData = item.tpData[0];
        pirData.push({
          sku: tpData.sku,
          sku_description: row.sku_description,
          buying_country: tpData.buyer.country,
          country: tpData.buyer.country,
          brewer_country: tpData.brewer.country,
          buyer_code: tpData.buyer.code,
          brewer_code: tpData.brewer.code,
          currency: tpData.currency.currency_code,
          interco_markup_out_perf: tpData.interco_markup_out_perf,
          transfer_price: tpData.total_tp,
        });
      });

      setForm(formSelections);
      if (errorRows.length > 0)
        showAlert(
          {
            message:
              errorRows.length > 1
                ? `No PIR extension data found for rows ${errorRows.join(', ')}`
                : `No PIR extension data found.`,
          },
          'error',
        );

      setPir(pirData);
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    try {
      setSaveLoading(true);
      const data = await submitInfo(pir);
      showAlert({ message: 'PIR has been completed successfully' }, 'success');
      reset();

      navigate(routes.extensions(data.request_id).info);
    } catch (e) {
      showAlert(e, 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <Context.Provider
      value={{
        skus,
        countries,
        form,
        setForm,
        canSubmit,
        codes,
        loading,
        fetch,
        pir,
        saveLoading,
        submit,
        onFormChange,
        codesCountry,
        initialLoading,
      }}
    >
      {children}
    </Context.Provider>
  );
};
